import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import useFormState from '../../hooks/useFormState.js';
import ServiceRatesCard from './ServiceRatesCard.jsx';

const EMPTY = {
  name: 'Standard Hourly Towage',
  baseHourlyRate: '',
  vatPercent: '12',
  surcharges: [],
  tiers: [],
  activeFrom: '',
  activeTo: '',
  currency: 'USD',
  customerUserId: '',
};

function statusBadge(status) {
  if (status === 'Active') return 'bg-success';
  if (status === 'Scheduled') return 'bg-info text-dark';
  return 'bg-secondary';
}

export default function TariffRateCardsPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const { form, setForm, errors, setErrors, setField, clearErrors } = useFormState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [penaltyForm, setPenaltyForm] = useState({
    lateCancelThresholdHours: '',
    cancellationChargeType: 'flat',
    cancellationPenalty: '',
    cancellationMinHours: '',
    noShowPenalty: '',
  });
  const [penaltyMsg, setPenaltyMsg] = useState(null);
  const [penaltyErrors, setPenaltyErrors] = useState({});
  const [customers, setCustomers] = useState([]);

  const load = useCallback(async () => {
    const [cardsRes, penRes, custRes] = await Promise.all([
      api.listRateCards(),
      api.getTariffPenalties(),
      api.getTariffCustomers().catch(() => []),
    ]);
    setCards(cardsRes || []);
    setCustomers(custRes || []);
    if (penRes) {
      setPenaltyForm({
        lateCancelThresholdHours: String(penRes.late_cancel_threshold_hours ?? ''),
        cancellationChargeType: penRes.cancellation_charge_type || 'flat',
        cancellationPenalty: String(penRes.cancellation_penalty ?? ''),
        cancellationMinHours: String(penRes.cancellation_min_hours ?? ''),
        noShowPenalty: String(penRes.no_show_penalty ?? ''),
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load tariffs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const openCreate = () => {
    setErrors({});
    setForm(EMPTY);
    setModalOpen(true);
  };

  const cloneFrom = (card) => {
    setErrors({});
    // Start from EMPTY so every key the modal renders (tiers, customerUserId) exists.
    setForm({
      ...EMPTY,
      name: card.name,
      baseHourlyRate: String(card.base_hourly_rate),
      vatPercent: String(card.vat_percent),
      surcharges: (card.surcharges || []).map((s) => ({ label: s.label, percent: String(s.percent) })),
      tiers: (card.tiers || []).map((t) => ({
        vesselType: t.vesselType ?? '',
        gtMin: t.gtMin ?? '',
        gtMax: t.gtMax ?? '',
        hourlyRate: t.hourlyRate != null ? String(t.hourlyRate) : '',
      })),
      customerUserId: card.customer_user_id || '',
      activeFrom: '',
      activeTo: '',
      currency: card.currency || 'USD',
    });
    setModalOpen(true);
  };

  const addSurcharge = () =>
    setForm((f) => ({ ...f, surcharges: [...f.surcharges, { label: '', percent: '' }] }));
  const updateSurcharge = (i, key, value) => {
    setForm((f) => ({
      ...f,
      surcharges: f.surcharges.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
    }));
    clearErrors(`surcharge_${i}`);
  };
  const removeSurcharge = (i) => {
    setForm((f) => ({ ...f, surcharges: f.surcharges.filter((_, idx) => idx !== i) }));
    // Row indexes shift after a removal, so drop all per-row surcharge errors.
    setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith('surcharge_'))));
  };

  // FR-RAT-2 tier rows (by GT band / boat type → hourly rate).
  const addTier = () =>
    setForm((f) => ({ ...f, tiers: [...f.tiers, { vesselType: '', gtMin: '', gtMax: '', hourlyRate: '' }] }));
  const updateTier = (i, key, value) =>
    setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)) }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Tariff name is required';
    if (!form.baseHourlyRate || Number(form.baseHourlyRate) <= 0)
      next.baseHourlyRate = 'Base hourly rate must be positive';
    if (form.vatPercent === '' || Number(form.vatPercent) < 0 || Number(form.vatPercent) > 100)
      next.vatPercent = 'VAT must be between 0 and 100';
    if (!form.activeFrom) next.activeFrom = 'Active start date is required';
    if (form.activeTo && form.activeTo < form.activeFrom) next.activeTo = 'End date must be after start';
    form.surcharges.forEach((s, i) => {
      if (s.percent === '') return;
      if (Number(s.percent) < 0) next[`surcharge_${i}`] = 'Surcharge % cannot be negative';
      else if (Number(s.percent) > 1000) next[`surcharge_${i}`] = 'Surcharge % cannot exceed 1000';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setPageMsg(null);
    try {
      const surcharges = form.surcharges
        .filter((s) => s.label.trim() && s.percent !== '')
        .map((s) => ({ label: s.label.trim(), percent: Number(s.percent) }));
      const created = await api.createRateCard({
        name: form.name.trim(),
        baseHourlyRate: Number(form.baseHourlyRate),
        vatPercent: Number(form.vatPercent),
        surcharges,
        activeFrom: form.activeFrom,
        activeTo: form.activeTo || null,
        currency: form.currency,
        customerUserId: form.customerUserId || null,
        tiers: form.tiers
          .filter((t) => t.hourlyRate !== '' && Number(t.hourlyRate) > 0)
          .map((t) => ({
            vesselType: t.vesselType?.trim() || null,
            gtMin: t.gtMin !== '' ? Number(t.gtMin) : null,
            gtMax: t.gtMax !== '' ? Number(t.gtMax) : null,
            hourlyRate: Number(t.hourlyRate),
          })),
      });
      setPageMsg({
        type: 'success',
        text: `Rate card saved as version ${created.version} (${created.status}).`,
      });
      await load();
      setModalOpen(false);
    } catch (err) {
      setErrors((p) => ({ ...p, form: err.message || 'Failed to save rate card.' }));
    } finally {
      setSaving(false);
    }
  };

  const setPenaltyField = (key, value) => {
    setPenaltyForm((f) => ({ ...f, [key]: value }));
    setPenaltyErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Mirrors penaltySettingsSchema on the backend so the user sees a field-level message.
  const validatePenalties = () => {
    const next = {};
    const p = penaltyForm;
    const threshold = Number(p.lateCancelThresholdHours);
    if (p.lateCancelThresholdHours === '') next.lateCancelThresholdHours = 'Threshold is required';
    else if (threshold < 0) next.lateCancelThresholdHours = 'Threshold cannot be negative';
    else if (threshold > 168) next.lateCancelThresholdHours = 'Threshold cannot exceed 168 hours';
    if (p.cancellationChargeType === 'flat') {
      if (p.cancellationPenalty === '' || Number(p.cancellationPenalty) <= 0)
        next.cancellationPenalty = 'Flat cancellation penalty must be greater than 0';
    } else if (p.cancellationMinHours === '' || Number(p.cancellationMinHours) <= 0) {
      next.cancellationMinHours = 'Minimum chargeable hours must be greater than 0';
    }
    if (p.noShowPenalty === '') next.noShowPenalty = 'No-show penalty is required';
    else if (Number(p.noShowPenalty) < 0) next.noShowPenalty = 'No-show penalty cannot be negative';
    setPenaltyErrors(next);
    return Object.keys(next).length === 0;
  };

  const savePenalties = async (e) => {
    e.preventDefault();
    setPenaltyMsg(null);
    if (!validatePenalties()) return;
    try {
      await api.updateTariffPenalties({
        lateCancelThresholdHours: Number(penaltyForm.lateCancelThresholdHours),
        cancellationChargeType: penaltyForm.cancellationChargeType,
        cancellationPenalty: Number(penaltyForm.cancellationPenalty || 0),
        cancellationMinHours: Number(penaltyForm.cancellationMinHours || 0),
        noShowPenalty: Number(penaltyForm.noShowPenalty),
      });
      setPenaltyMsg({ type: 'success', text: 'Charging penalties updated.' });
      await load();
    } catch (err) {
      setPenaltyMsg({ type: 'danger', text: err.message || 'Failed to update penalties.' });
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="tariffs-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-file-invoice-dollar me-2" aria-hidden="true" />
                Tariff Rate Cards &amp; Version Manager
              </h3>
              <p className="text-muted mb-0 small">
                Create and version hourly towage rate cards, VAT and surcharges, and the service tariffs per port and
                assistance area.
              </p>
            </div>
            <span className="badge bg-primary p-2">Billing Currency: USD / PHP</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="tariffs-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="tariff-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: tariff versions registry */}
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-history me-2" aria-hidden="true" />
                  Tariff Versions Registry
                </h5>
                <button className="btn btn-sm btn-primary" onClick={openCreate} data-testid="create-rate-card">
                  <i className="fas fa-plus me-1" aria-hidden="true" />
                  Create Rate Card
                </button>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Tariff Name</th>
                        <th>Account</th>
                        <th>Ver</th>
                        <th>Base Rate</th>
                        <th>VAT %</th>
                        <th>Active Window</th>
                        <th>Status</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {cards.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            No rate cards yet.
                          </td>
                        </tr>
                      )}
                      {cards.map((c) => (
                        <tr key={c.id} data-testid="rate-card-row" data-name={c.name} data-version={c.version}>
                          <td className="fw-bold text-dark">{c.name}</td>
                          <td data-testid="rate-card-account">
                            {c.scope === 'Account' ? (
                              <span className="badge bg-primary">{c.accountName || 'Account'}</span>
                            ) : (
                              <span className="badge bg-light text-secondary border">Standard</span>
                            )}
                          </td>
                          <td data-testid="rate-card-version">v{c.version}</td>
                          <td>
                            {c.currency} {Number(c.base_hourly_rate)}
                          </td>
                          <td>{Number(c.vat_percent)}%</td>
                          <td className="small">
                            {c.active_from} → {c.active_to || '—'}
                          </td>
                          <td>
                            <span className={`badge ${statusBadge(c.status)}`} data-testid="rate-card-status">
                              {c.status}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => cloneFrom(c)}
                              data-testid="clone-rate-card"
                              aria-label={`Clone ${c.name} v${c.version}`}
                            >
                              <i className="fas fa-clone me-1" aria-hidden="true" />
                              Clone
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: penalties */}
          <div className="col-lg-4 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-ban me-2 text-danger" aria-hidden="true" />
                  No-Show &amp; Late Cancellations
                </h5>
              </div>
              <div className="card-body">
                {penaltyMsg && (
                  <div className={`alert alert-${penaltyMsg.type} py-2`} role="alert" data-testid="penalty-message">
                    {penaltyMsg.text}
                  </div>
                )}
                <form onSubmit={savePenalties} noValidate>
                  <div className="mb-3">
                    <label htmlFor="p-threshold" className="form-label small fw-bold text-muted">
                      Late Cancellation Threshold (Hrs)
                    </label>
                    <input
                      id="p-threshold"
                      type="number"
                      step="0.5"
                      className={`form-control form-control-sm${penaltyErrors.lateCancelThresholdHours ? ' is-invalid' : ''}`}
                      value={penaltyForm.lateCancelThresholdHours}
                      onChange={(e) => setPenaltyField('lateCancelThresholdHours', e.target.value)}
                      data-testid="penalty-threshold"
                    />
                    {penaltyErrors.lateCancelThresholdHours && (
                      <div className="invalid-feedback" data-testid="penalty-threshold-error">{penaltyErrors.lateCancelThresholdHours}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="p-charge-type" className="form-label small fw-bold text-muted">
                      Late Cancellation Charge Basis
                    </label>
                    <select
                      id="p-charge-type"
                      className="form-select form-select-sm"
                      value={penaltyForm.cancellationChargeType}
                      onChange={(e) => {
                        setPenaltyField('cancellationChargeType', e.target.value);
                        setPenaltyErrors((prev) => {
                          const next = { ...prev };
                          delete next.cancellationPenalty;
                          delete next.cancellationMinHours;
                          return next;
                        });
                      }}
                      data-testid="penalty-charge-type"
                    >
                      <option value="flat">Flat Fee ($)</option>
                      <option value="min_hours">Minimum Chargeable Hours</option>
                    </select>
                  </div>
                  {penaltyForm.cancellationChargeType === 'flat' ? (
                    <div className="mb-3">
                      <label htmlFor="p-cancel" className="form-label small fw-bold text-muted">
                        Cancellation Flat Penalty ($)
                      </label>
                      <input
                        id="p-cancel"
                        type="number"
                        step="1"
                        className={`form-control form-control-sm${penaltyErrors.cancellationPenalty ? ' is-invalid' : ''}`}
                        value={penaltyForm.cancellationPenalty}
                        onChange={(e) => setPenaltyField('cancellationPenalty', e.target.value)}
                        data-testid="penalty-cancel"
                      />
                      {penaltyErrors.cancellationPenalty && (
                        <div className="invalid-feedback" data-testid="penalty-cancel-error">{penaltyErrors.cancellationPenalty}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-3">
                      <label htmlFor="p-minhours" className="form-label small fw-bold text-muted">
                        Minimum Chargeable Hours
                      </label>
                      <input
                        id="p-minhours"
                        type="number"
                        step="0.5"
                        className={`form-control form-control-sm${penaltyErrors.cancellationMinHours ? ' is-invalid' : ''}`}
                        value={penaltyForm.cancellationMinHours}
                        onChange={(e) => setPenaltyField('cancellationMinHours', e.target.value)}
                        data-testid="penalty-min-hours"
                      />
                      {penaltyErrors.cancellationMinHours && (
                        <div className="invalid-feedback" data-testid="penalty-min-hours-error">{penaltyErrors.cancellationMinHours}</div>
                      )}
                    </div>
                  )}
                  <div className="mb-3">
                    <label htmlFor="p-noshow" className="form-label small fw-bold text-muted">
                      No-Show Penalty ($)
                    </label>
                    <input
                      id="p-noshow"
                      type="number"
                      step="1"
                      className={`form-control form-control-sm${penaltyErrors.noShowPenalty ? ' is-invalid' : ''}`}
                      value={penaltyForm.noShowPenalty}
                      onChange={(e) => setPenaltyField('noShowPenalty', e.target.value)}
                      data-testid="penalty-noshow"
                    />
                    {penaltyErrors.noShowPenalty && (
                      <div className="invalid-feedback" data-testid="penalty-noshow-error">{penaltyErrors.noShowPenalty}</div>
                    )}
                  </div>
                  <button className="btn btn-primary btn-sm w-100" data-testid="save-penalties">
                    <i className="fas fa-save me-1" aria-hidden="true" />
                    Apply Charging Penalties
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* 8950: service tariffs by port and assistance area */}
          <div className="col-12">
            <ServiceRatesCard />
          </div>
        </div>
      )}

      {/* Create / Version modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="rate-card-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-file-invoice-dollar me-2 text-primary" aria-hidden="true" />
                      Create / Version Rate Card
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && <div className="alert alert-danger">{errors.form}</div>}
                    <div className="row">
                      <div className="col-md-8 mb-3">
                        <label htmlFor="t-name" className="form-label small fw-bold text-muted">
                          Tariff Name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="t-name"
                          className={`form-control form-control-sm${errors.name ? ' is-invalid' : ''}`}
                          value={form.name}
                          onChange={(e) => setField('name', e.target.value)}
                          data-testid="tariff-name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="t-currency" className="form-label small fw-bold text-muted">
                          Currency
                        </label>
                        <select
                          id="t-currency"
                          className="form-select form-select-sm"
                          value={form.currency}
                          onChange={(e) => setField('currency', e.target.value)}
                          data-testid="tariff-currency"
                        >
                          <option value="USD">USD</option>
                          <option value="PHP">PHP</option>
                        </select>
                      </div>
                      <div className="col-md-8 mb-3">
                        <label htmlFor="t-account" className="form-label small fw-bold text-muted">
                          Account Scope (FR-RAT-4)
                        </label>
                        <select
                          id="t-account"
                          className="form-select form-select-sm"
                          value={form.customerUserId}
                          onChange={(e) => setField('customerUserId', e.target.value)}
                          data-testid="tariff-account"
                        >
                          <option value="">— Standard (all customers) —</option>
                          {customers.map((c) => (
                            <option key={c.id} value={c.id}>Negotiated — {c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label htmlFor="t-base" className="form-label small fw-bold text-muted">
                          Base Hourly Rate <span className="text-danger">*</span>
                        </label>
                        <input
                          id="t-base"
                          type="number"
                          step="0.01"
                          className={`form-control form-control-sm${errors.baseHourlyRate ? ' is-invalid' : ''}`}
                          value={form.baseHourlyRate}
                          onChange={(e) => setField('baseHourlyRate', e.target.value)}
                          data-testid="base-rate"
                        />
                        {errors.baseHourlyRate && <div className="invalid-feedback">{errors.baseHourlyRate}</div>}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="t-vat" className="form-label small fw-bold text-muted">
                          VAT % <span className="text-danger">*</span>
                        </label>
                        <select
                          id="t-vat"
                          className={`form-select form-select-sm${errors.vatPercent ? ' is-invalid' : ''}`}
                          value={form.vatPercent}
                          onChange={(e) => setField('vatPercent', e.target.value)}
                          data-testid="vat-percent"
                        >
                          <option value="0">0%</option>
                          <option value="12">12%</option>
                          <option value="15">15%</option>
                          <option value="20">20%</option>
                        </select>
                        {errors.vatPercent && <div className="invalid-feedback">{errors.vatPercent}</div>}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="t-from" className="form-label small fw-bold text-muted">
                          Active From <span className="text-danger">*</span>
                        </label>
                        <input
                          id="t-from"
                          type="date"
                          className={`form-control form-control-sm${errors.activeFrom ? ' is-invalid' : ''}`}
                          value={form.activeFrom}
                          onChange={(e) => setField('activeFrom', e.target.value, 'activeTo')}
                          data-testid="active-from"
                        />
                        {errors.activeFrom && <div className="invalid-feedback">{errors.activeFrom}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="t-to" className="form-label small fw-bold text-muted">
                          Active To
                        </label>
                        <input
                          id="t-to"
                          type="date"
                          className={`form-control form-control-sm${errors.activeTo ? ' is-invalid' : ''}`}
                          value={form.activeTo}
                          onChange={(e) => setField('activeTo', e.target.value)}
                          data-testid="active-to"
                        />
                        {errors.activeTo && <div className="invalid-feedback">{errors.activeTo}</div>}
                      </div>
                    </div>

                    <div className="mb-2 d-flex justify-content-between align-items-center">
                      <label className="form-label small fw-bold text-muted mb-0">
                        Surcharges (Sunday/Holiday, Night Shift, …)
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={addSurcharge}
                        data-testid="add-surcharge"
                      >
                        <i className="fas fa-plus" aria-hidden="true" /> Add
                      </button>
                    </div>
                    {form.surcharges.map((s, i) => (
                      <div className="row g-2 mb-2" key={i}>
                        <div className="col-7">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Label"
                            value={s.label}
                            onChange={(e) => updateSurcharge(i, 'label', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <input
                            type="number"
                            step="0.1"
                            className={`form-control form-control-sm${errors[`surcharge_${i}`] ? ' is-invalid' : ''}`}
                            placeholder="%"
                            value={s.percent}
                            onChange={(e) => updateSurcharge(i, 'percent', e.target.value)}
                            data-testid="surcharge-percent"
                          />
                          {errors[`surcharge_${i}`] && (
                            <div className="invalid-feedback" data-testid="surcharge-percent-error">
                              {errors[`surcharge_${i}`]}
                            </div>
                          )}
                        </div>
                        <div className="col-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeSurcharge(i)}
                            aria-label="Remove surcharge"
                          >
                            <i className="fas fa-times" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* FR-RAT-2: tiered rates by boat type / GT band */}
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label small fw-bold text-muted mb-0">
                        Tiered Rates by GT / Boat Type (optional)
                      </label>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addTier} data-testid="add-tier">
                        <i className="fas fa-plus me-1" aria-hidden="true" />Add Tier
                      </button>
                    </div>
                    <p className="text-muted small mb-2">
                      When a job matches a tier (boat type / GT band), its hourly rate is used instead of the base.
                    </p>
                    {form.tiers.map((t, i) => (
                      <div className="row g-1 mb-1 align-items-center" key={i} data-testid="tier-row">
                        <div className="col-3">
                          <input className="form-control form-control-sm" placeholder="Boat type" value={t.vesselType}
                            onChange={(e) => updateTier(i, 'vesselType', e.target.value)} data-testid="tier-type" />
                        </div>
                        <div className="col-2">
                          <input type="number" className="form-control form-control-sm" placeholder="GT min" value={t.gtMin}
                            onChange={(e) => updateTier(i, 'gtMin', e.target.value)} data-testid="tier-gtmin" />
                        </div>
                        <div className="col-2">
                          <input type="number" className="form-control form-control-sm" placeholder="GT max" value={t.gtMax}
                            onChange={(e) => updateTier(i, 'gtMax', e.target.value)} data-testid="tier-gtmax" />
                        </div>
                        <div className="col-4">
                          <input type="number" className="form-control form-control-sm" placeholder="Hourly rate" value={t.hourlyRate}
                            onChange={(e) => updateTier(i, 'hourlyRate', e.target.value)} data-testid="tier-rate" />
                        </div>
                        <div className="col-1">
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTier(i)} aria-label="Remove tier">
                            <i className="fas fa-times" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-rate-card">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save and Version Rate Card
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
